import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useUser } from '@/providers';
import {
  TableRow,
  TableCell,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Stack,
  Box,
} from '@mui/material';

import ScheduleIcon from '@mui/icons-material/Schedule';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import StopIcon from '@mui/icons-material/Stop';
import CheckIcon from '@mui/icons-material/Check';

import api from '@/apis/api';
import STATUSES from '@/components/statuses';
import { updateJobWorker } from '@/actions/jobs';

export default function JobWorker({ number, job_uuid, worker }) {
  const dispatch = useDispatch();
  const user = useUser();

  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(false);
  const [stopping, setStopping] = useState(false);

  const handleDownload = async () => {
    if (!user) return;
    setDownloading(true);
    try {
      let response = await api.get(
        `/job/${job_uuid}/worker/${worker.job_worker_uuid}/logs`,
        { params: { project_uuid: user.selected_project } },
      );
      window.location.href = response.data;
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setError(true);
      }
    }
    setDownloading(false);
  };

  const renderStatus = (status) => {
    if (
      [
        STATUSES.NEW,
        STATUSES.STARTING,
        STATUSES.PROVISIONING,
        STATUSES.STOPPING,
        STATUSES.REMOVING,
        STATUSES.BUILDING,
        STATUSES.DOWNLOADING,
        STATUSES.CACHING,
        STATUSES.UPLOADING,
      ].includes(status)
    ) {
      return <CircularProgress size={24} />;
    }
    if ([STATUSES.WAITING_DATA, STATUSES.WAITING_GPU].includes(status)) {
      return <ScheduleIcon color='action' fontSize='large' />;
    }
    if (status === STATUSES.STOPPED) {
      return <StopCircleOutlinedIcon color='disabled' fontSize='large' />;
    }
    if (status === STATUSES.FINISHED) {
      return <CheckCircleOutlinedIcon color='disabled' fontSize='large' />;
    }
    if (status === STATUSES.RUNNING || status === STATUSES.READY) {
      return <PlayCircleIcon color='success' fontSize='large' />;
    }
    if (status === STATUSES.FAILED) {
      return <ErrorIcon color='error' fontSize='large' />;
    }
    return null;
  };

  if (!worker || !worker.job_worker_uuid) {
    return null;
  }

  return (
    <>
      <TableRow key={worker.job_worker_uuid}>
        <TableCell>
          <Stack direction='row' spacing={2} alignItems='center'>
            <Typography variant='h6'>Worker {number}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {renderStatus(worker?.status)}
            </Box>
            <Typography
              variant='body2'
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <b>{worker?.status || ''}</b>
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{worker?.command || ''}</TableCell>
        <TableCell align='right'>
          <Stack direction='row' spacing={1} justifyContent='flex-end'>
            {[
              STATUSES.RUNNING,
              STATUSES.UPLOADING,
              STATUSES.WAITING_GPU,
            ].includes(worker?.status) ? (
              <Button
                variant='outlined'
                size='small'
                startIcon={
                  stopping ? <CircularProgress size={20} /> : <StopIcon />
                }
                onClick={async (e) => {
                  e.preventDefault();
                  setStopping(true);
                  await dispatch(
                    updateJobWorker(
                      user.selected_project,
                      job_uuid,
                      worker.job_worker_uuid,
                      {
                        command: 'stop',
                      },
                    ),
                  );
                  setStopping(false);
                }}
                disabled={stopping}
              >
                Stop
              </Button>
            ) : undefined}
            {worker?.status === STATUSES.FINISHED ? (
              <Button
                variant='outlined'
                size='small'
                startIcon={
                  downloading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CloudDownloadIcon />
                  )
                }
                onClick={handleDownload}
                disabled={downloading}
              >
                Download Logs
              </Button>
            ) : undefined}
          </Stack>
        </TableCell>
      </TableRow>

      <Dialog
        open={error}
        onClose={() => setError(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>Logs Not Available</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Worker {number}'s log extract is not currently available. If the
            worker recently finished, the log extract may still be generating.
            If the worker finished more than 7 days ago, the log extract may
            have been purged.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant='contained'
            startIcon={<CheckIcon />}
            onClick={() => setError(false)}
          >
            Okay
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
